import { createDemo } from '@mui/internal-core-docs/utils/createDemo';
import ReactVirtualizedTable from './ReactVirtualizedTable';
import ClientProvider from './client';
export default createDemo(import.meta.url, ReactVirtualizedTable, { ClientProvider, precompute: {
  "Default": {
    "url": "file:///opt/build/repo/docs/data/material/components/table/demos/react-virtualized/ReactVirtualizedTable.tsx",
    "fileName": "ReactVirtualizedTable.tsx",
    "language": "tsx",
    "source": {
      "type": "root",
      "children": [
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "import * as React from 'react';\nimport Table from '@mui/material/Table';\nimport TableBody from '@mui/material/TableBody';\nimport TableCell from '@mui/material/TableCell';\nimport TableContainer from '@mui/material/TableContainer';\nimport TableHead from '@mui/material/TableHead';\nimport TableRow from '@mui/material/TableRow';\nimport Paper from '@mui/material/Paper';\nimport { TableVirtuoso } from 'react-virtuoso';\nimport type { TableComponents } from 'react-virtuoso';\nimport Chance from 'chance';\n\ninterface Data {\n  id: number;\n  firstName: string;\n  lastName: string;\n  age: number;\n  phone: string;\n  state: string;\n}\n\ninterface ColumnData {\n  dataKey: keyof Data;\n  label: string;\n  numeric?: boolean;\n  width?: number;\n}\n\nconst chance = new Chance(42);\n\nfunction createData(id: number): Data {\n  return {\n    id,\n    firstName: chance.first(),\n    lastName: chance.last(),\n    age: chance.age(),\n    phone: chance.phone(),\n    state: chance.state({ full: true }),\n  };\n}\n\nconst columns: ColumnData[] = [\n  {\n    width: 100,\n    label: 'First Name',\n    dataKey: 'firstName',\n  },\n  {\n    width: 100,\n    label: 'Last Name',\n    dataKey: 'lastName',\n  },\n  {\n    width: 50,\n    label: 'Age',\n    dataKey: 'age',\n    numeric: true,\n  },\n  {\n    width: 110,\n    label: 'State',\n    dataKey: 'state',\n  },\n  {\n    width: 130,\n    label: 'Phone Number',\n    dataKey: 'phone',\n  },\n];\n\nconst rows: Data[] = Array.from({ length: 200 }, (_, index) => createData(index));\n\nconst VirtuosoTableComponents: TableComponents<Data> = {\n  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (\n    <TableContainer component={Paper} {...props} ref={ref} />\n  )),\n  Table: (props) => (\n    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />\n  ),\n  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (\n    <TableHead {...props} ref={ref} />\n  )),\n  TableRow,\n  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (\n    <TableBody {...props} ref={ref} />\n  )),\n};\n\nfunction fixedHeaderContent() {\n  return (\n    <TableRow>\n      {columns.map((column) => (\n        <TableCell\n          key={column.dataKey}\n          variant=\"head\"\n          align={column.numeric || false ? 'right' : 'left'}\n          style={{ width: column.width }}\n          sx={{ backgroundColor: 'background.paper' }}\n        >\n          {column.label}\n        </TableCell>\n      ))}\n    </TableRow>\n  );\n}\n\nfunction rowContent(_index: number, row: Data) {\n  return (\n    <React.Fragment>\n      {columns.map((column) => (\n        <TableCell\n          key={column.dataKey}\n          component={column.dataKey === 'firstName' ? 'th' : 'td'}\n          scope={column.dataKey === 'firstName' ? 'row' : undefined}\n          align={column.numeric || false ? 'right' : 'left'}\n        >\n          {row[column.dataKey]}\n        </TableCell>\n      ))}\n    </React.Fragment>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  );\n}\n\nexport default function ReactVirtualizedTable() {\n  return (\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "dataFrameType": "focus",
            "dataFrameIndent": 2,
            "dataFrameFocusTarget": "",
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "    <Paper style={{ height: 400, width: '100%' }}>\n      <TableVirtuoso\n        data={rows}\n        components={VirtuosoTableComponents}\n        fixedHeaderContent={fixedHeaderContent}\n        itemContent={rowContent}\n      />\n    </Paper>\n"
            }
          ]
        },
        {
          "type": "element",
          "tagName": "span",
          "properties": {
            "className": [
              "frame"
            ]
          },
          "children": [
            {
              "type": "text",
              "value": "  );\n}\n"
            }
          ]
        }
      ],
      "data": {
        "totalLines": 135,
        "focusedLines": 8,
        "collapsible": true
      }
    },
    "fallback": [
      [
        "span",
        "frame",
        "import * as React from 'react';\nimport Table from '@mui/material/Table';\nimport TableBody from '@mui/material/TableBody';\nimport TableCell from '@mui/material/TableCell';\nimport TableContainer from '@mui/material/TableContainer';\nimport TableHead from '@mui/material/TableHead';\nimport TableRow from '@mui/material/TableRow';\nimport Paper from '@mui/material/Paper';\nimport { TableVirtuoso } from 'react-virtuoso';\nimport type { TableComponents } from 'react-virtuoso';\nimport Chance from 'chance';\n\ninterface Data {\n  id: number;\n  firstName: string;\n  lastName: string;\n  age: number;\n  phone: string;\n  state: string;\n}\n\ninterface ColumnData {\n  dataKey: keyof Data;\n  label: string;\n  numeric?: boolean;\n  width?: number;\n}\n\nconst chance = new Chance(42);\n\nfunction createData(id: number): Data {\n  return {\n    id,\n    firstName: chance.first(),\n    lastName: chance.last(),\n    age: chance.age(),\n    phone: chance.phone(),\n    state: chance.state({ full: true }),\n  };\n}\n\nconst columns: ColumnData[] = [\n  {\n    width: 100,\n    label: 'First Name',\n    dataKey: 'firstName',\n  },\n  {\n    width: 100,\n    label: 'Last Name',\n    dataKey: 'lastName',\n  },\n  {\n    width: 50,\n    label: 'Age',\n    dataKey: 'age',\n    numeric: true,\n  },\n  {\n    width: 110,\n    label: 'State',\n    dataKey: 'state',\n  },\n  {\n    width: 130,\n    label: 'Phone Number',\n    dataKey: 'phone',\n  },\n];\n\nconst rows: Data[] = Array.from({ length: 200 }, (_, index) => createData(index));\n\nconst VirtuosoTableComponents: TableComponents<Data> = {\n  Scroller: React.forwardRef<HTMLDivElement>((props, ref) => (\n    <TableContainer component={Paper} {...props} ref={ref} />\n  )),\n  Table: (props) => (\n    <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />\n  ),\n  TableHead: React.forwardRef<HTMLTableSectionElement>((props, ref) => (\n    <TableHead {...props} ref={ref} />\n  )),\n  TableRow,\n  TableBody: React.forwardRef<HTMLTableSectionElement>((props, ref) => (\n    <TableBody {...props} ref={ref} />\n  )),\n};\n\nfunction fixedHeaderContent() {\n  return (\n    <TableRow>\n      {columns.map((column) => (\n        <TableCell\n          key={column.dataKey}\n          variant=\"head\"\n          align={column.numeric || false ? 'right' : 'left'}\n          style={{ width: column.width }}\n          sx={{ backgroundColor: 'background.paper' }}\n        >\n          {column.label}\n        </TableCell>\n      ))}\n    </TableRow>\n  );\n}\n\nfunction rowContent(_index: number, row: Data) {\n  return (\n    <React.Fragment>\n      {columns.map((column) => (\n        <TableCell\n          key={column.dataKey}\n          component={column.dataKey === 'firstName' ? 'th' : 'td'}\n          scope={column.dataKey === 'firstName' ? 'row' : undefined}\n          align={column.numeric || false ? 'right' : 'left'}\n        >\n          {row[column.dataKey]}\n        </TableCell>\n      ))}\n    </React.Fragment>\n"
      ],
      [
        "span",
        "frame",
        "  );\n}\n\nexport default function ReactVirtualizedTable() {\n  return (\n"
      ],
      [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        "    <Paper style={{ height: 400, width: '100%' }}>\n      <TableVirtuoso\n        data={rows}\n        components={VirtuosoTableComponents}\n        fixedHeaderContent={fixedHeaderContent}\n        itemContent={rowContent}\n      />\n    </Paper>\n"
      ],
      [
        "span",
        "frame",
        "  );\n}\n"
      ]
    ],
    "fallbackCritical": {
      "2": [
        "span",
        "frame",
        {
          "dataFrameType": "focus",
          "dataFrameIndent": 2,
          "dataFrameFocusTarget": ""
        },
        [
          [
            "span",
            "line",
            {
              "dataLn": 126
            },
            [
              "    <",
              [
                "span",
                "pl-c1 di-jsx",
                "Paper"
              ],
              " ",
              [
                "span",
                "pl-e di-ak",
                "style"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              "{ ",
              [
                "span",
                "di-op di-jv",
                "height"
              ],
              ": ",
              [
                "span",
                "pl-c1 di-num",
                "400"
              ],
              ", ",
              [
                "span",
                "di-op di-jv",
                "width"
              ],
              ": ",
              [
                "span",
                "pl-s",
                [
                  [
                    "span",
                    "pl-pds",
                    "'"
                  ],
                  "100%",
                  [
                    "span",
                    "pl-pds",
                    "'"
                  ]
                ]
              ],
              " }",
              [
                "span",
                "pl-pse",
                "}"
              ],
              ">"
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 127
            },
            [
              "      <",
              [
                "span",
                "pl-c1 di-jsx",
                "TableVirtuoso"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 128
            },
            [
              "        ",
              [
                "span",
                "pl-e di-ak",
                "data"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "rows"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 129
            },
            [
              "        ",
              [
                "span",
                "pl-e di-ak",
                "components"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "VirtuosoTableComponents"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 130
            },
            [
              "        ",
              [
                "span",
                "pl-e di-ak",
                "fixedHeaderContent"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "fixedHeaderContent"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 131
            },
            [
              "        ",
              [
                "span",
                "pl-e di-ak",
                "itemContent"
              ],
              [
                "span",
                "pl-k di-pu di-ae",
                "="
              ],
              [
                "span",
                "pl-pse",
                "{"
              ],
              [
                "span",
                "pl-smi di-jv",
                "rowContent"
              ],
              [
                "span",
                "pl-pse",
                "}"
              ]
            ]
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 132
            },
            "      />"
          ],
          "\n",
          [
            "span",
            "line",
            {
              "dataLn": 133
            },
            [
              "    </",
              [
                "span",
                "pl-c1 di-jsx",
                "Paper"
              ],
              ">"
            ]
          ],
          "\n"
        ]
      ]
    },
    "totalLines": 135,
    "focusedLines": 8,
    "collapsible": true,
    "externals": [
      "react",
      "@mui/material/Table",
      "@mui/material/TableBody",
      "@mui/material/TableCell",
      "@mui/material/TableContainer",
      "@mui/material/TableHead",
      "@mui/material/TableRow",
      "@mui/material/Paper",
      "react-virtuoso",
      "chance"
    ]
  }
}, loadPrecompute: () => import("./index.ts?mui-deferred-precompute").then((module) => module.default) });
